import React from "react";
import PropTypes from "prop-types";
import { Card } from "@mui/material";

/**
 * PremiumCard - A reusable card component with premium styling
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to display inside the card
 * @param {string} props.gradient - Gradient style: 'primary', 'success', 'warning', 'error', 'info', 'purple'
 * @param {boolean} props.elevated - Add elevation effect
 * @param {Object} props.sx - Additional styles
 */
const PremiumCard = ({ children, gradient, elevated = false, sx = {}, ...rest }) => {
  const gradients = {
    primary: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    success: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
    warning: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    error: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    info: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    purple: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    blue: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
  };

  return (
    <Card
      sx={{
        borderRadius: "16px",
        boxShadow: elevated ? "0 8px 32px rgba(0, 0, 0, 0.12)" : "0 4px 16px rgba(0, 0, 0, 0.08)",
        background: gradient ? gradients[gradient] : "#ffffff",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        overflow: "hidden",
        "&:hover": elevated
          ? {
              transform: "translateY(-4px)",
              boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
            }
          : {},
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Card>
  );
};

const gradientOptions = ["primary", "success", "warning", "error", "info", "purple", "blue"];

PremiumCard.propTypes = {
  children: PropTypes.node.isRequired,
  gradient: PropTypes.oneOf([undefined, null, ...gradientOptions]),
  elevated: PropTypes.bool,
  sx: PropTypes.object,
};

PremiumCard.defaultProps = {
  gradient: undefined,
  elevated: false,
  sx: {},
};

export default PremiumCard;
